<!-- 講座詳細表示 -->
<script setup lang="ts">
  import draggable from "vuedraggable";
  import { marked } from "marked";
  import { storeToRefs } from "pinia";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { usePlanEditStore } from "~/stores/PlanEditStore";

  interface Props {
    planInfo: PlanInfo;
    isEdit: boolean; // 編集モードかどうか。編集モードの場合はtrue。編集モードの確認の場合はfalse。
    isView: boolean; // ビューモードかどうか。プラン詳細ページの場合はtrue
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  const planEditStore = usePlanEditStore();
  const { editBlocks, ownerContent, dividedContents, editPlan } = storeToRefs(planEditStore);

  const onAddBlock = (id?: number) => {
    planEditStore.addBlock(id, undefined);
  };

  // 見出しブロック内のデータを分解する
  const parseContents = (content: String) => {
    // 特定のタグで区切る
    const filterdContent: string = content
      .replace("<PlanOwner/>", "<SPLIT><PlanOwner/><SPLIT>")
      .replace("<PlanReviews/>", "<SPLIT><PlanReviews/><SPLIT>")
      .replace("<OwnerOtherPlans/>", "<SPLIT><OwnerOtherPlans/><SPLIT>")
      .replace("<BlockBrake/>", "<SPLIT><BlockBrake/><SPLIT>")
      .replace("<PlanEdit/>", "<SPLIT><PlanEdit/><SPLIT>")
      .replace("### **講座の評価**", "### **講座の体験談**") // 講座の評価を講座の体験談に置換
      .replace("### **プランの評価**", "### **講座の体験談**"); // プランの評価を講座の体験談に置換
    const contents: string[] = filterdContent.split("<SPLIT>");

    const contentArray: any[] = [];
    for (let i = 0; i < contents.length; i++) {
      if (contents[i] === "<PlanOwner/>") {
        contentArray.push({
          type: "owner",
          value: contents[i],
        });
      } else if (contents[i] === "<PlanReviews/>") {
        contentArray.push({
          type: "review",
          value: contents[i],
        });
      } else if (contents[i] === "<OwnerOtherPlans/>") {
        contentArray.push({
          type: "otherPlans",
          value: contents[i],
        });
      } else if (contents[i] === "<BlockBrake/>") {
        contentArray.push({
          type: "blockBrake",
          value: contents[i],
        });
      } else if (contents[i] !== "<PlanEdit/>") {
        contentArray.push({
          type: "md",
          value: contents[i],
        });
      }
    }

    if (content.includes("<PlanOwner/>")) {
      // オーナー情報の場合はownerContentに格納
      ownerContent.value = contentArray;
      return null;
    }

    // 講師プロフィール、講座の体験談、配信頻度、支払い方法、配信方法、料金、クーリングオフについて、会社概要、その他リンク、
    // 内部に<PlanReviews/>、<PlanOwner/>タグがあるブロック以外は編集可能とする
    if (
      !content.includes("### **講師プロフィール**") &&
      !content.includes("### **講座の体験談**") &&
      !content.includes("### **配信頻度**") &&
      !content.includes("### **支払い方法**") &&
      !content.includes("### **配信方法**") &&
      !content.includes("### **講座入会料金**") &&
      !content.includes("### **クーリングオフについて**") &&
      !content.includes("### **会社概要**") &&
      !content.includes("### **その他リンク**") &&
      !content.includes("<PlanReviews/>")
    ) {
      // 講師が編集できるブロックの場合はplanEditStoreに格納
      let description = marked(contentArray.reduce((acc, cur) => acc + cur.value, ""));
      // ui, liなどは改行があると表示されないのでreplace
      description = description.replace(/<ul>\n/g, "<ul>").replace(/<\/li>\n/g, "</li>");
      if (description) {
        planEditStore.addBlock(undefined, description);
      }
      return null;
    }

    // 講師が編集できないブロックの場合はcontentArrayを返す
    return contentArray;
  };

  // マークダウンの見出しで区切る
  const divideContents = (content: String) => {
    const contents = content
      .replaceAll("\n### ", "<DIVIDER>\n### ")
      .replaceAll("<BlockBrake/>", "<DIVIDER><BlockBrake/>")
      .replaceAll("<PlanEdit/>", "<DIVIDER><PlanEdit/>")
      .split("<DIVIDER>");
    return contents
      .map(content => {
        return parseContents(content);
      })
      .filter(Boolean);
  };

  // 表示用のコンテンツを取得。planInfoの設定によって非表示にするコンテンツを除外したものを返す
  const dividedContentsForView = computed(() => {
    let isAvailableReview = props.planInfo?.isAvailableReview;
    if (!props.isView) {
      // 講座編集画面ではeditPlanのisAvailableReviewを使う
      isAvailableReview = editPlan.value?.isAvailableReview ?? false;
    }
    if (!isAvailableReview) {
      // 体験談が非表示の場合は体験談を除外したものを返す
      return dividedContents.value
        .map(dividedContent => {
          // dividedContentのなかにreviewがある場合はnullを返す
          if (dividedContent.find(content => content.type === "review")) {
            return null;
          }
          return dividedContent;
        })
        .filter(Boolean);
    }
    return dividedContents.value;
  });

  const onRemoveBlock = (id: number) => {
    planEditStore.removeBlock(id);
  };

  const onSortBlock = (fromIndex: number, toIndex: number) => {
    planEditStore.sortBlock(fromIndex, toIndex);
  };

  onMounted(() => {
    if (!planEditStore.isEditing || props.isView) {
      planEditStore.editStart(props.planInfo);
      // subIntroductionに編集可能なブロックが入っている
      const list = divideContents(props.planInfo?.subIntroduction ?? "");
      // 編集可能なブロックと完全に分けたかったが、互換性のために以前のデータも同じように扱う
      dividedContents.value = list.concat(divideContents(props.planInfo?.introduction ?? ""));
    }
  });
</script>

<!-- markd を sanitize すると内容が崩れるのでそのまま -->
<!-- ref: https://github.com/Fincs-dev/fincs-webfront/pull/202 -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <div data-testid="plan-overview-markdown">
    <div class="px-1 text-left">
      <!-- オーナー情報 -->
      <div :class="isEdit ? 'tabContent-trans' : 'tabContent'">
        <div class="markdown">
          <h3>
            <strong>講師</strong>
          </h3>
          <br />
        </div>
        <OrganismsPlanOwner v-if="planInfo" :plan-owner="planInfo?.planOwner" class="mb-10" />
      </div>

      <!-- 編集可能エリア -->
      <template v-if="isEdit">
        <!-- エディター表示 -->
        <div v-if="editBlocks?.length" class="text-left cursor-pointer mb-4" @click="onAddBlock(editBlocks[0].id)">
          <v-icon icon="$mdiPlus" />
          ブロックを追加する
        </div>
        <draggable v-model="editBlocks" item-key="id" handle=".handle">
          <template #item="{ element: item }">
            <div class="tabContent">
              <OrganismsRichEditBlock
                :edit-block="item"
                :is-edit="isEdit"
                @update-text="
                  newText => {
                    item.text = newText;
                  }
                "
                @sort-block="onSortBlock"
                @remove-block="onRemoveBlock"
              />
            </div>
          </template>
        </draggable>
        <div class="text-left cursor-pointer mb-4" @click="onAddBlock(undefined)">
          <v-icon icon="$mdiPlus" />
          ブロックを追加する
        </div>
      </template>
      <template v-else>
        <!-- 通常表示 -->
        <v-sheet v-for="block in editBlocks" :key="block.id" class="tabContent">
          <OrganismsRichEditBlock :edit-block="block" :is-edit="isEdit" />
        </v-sheet>
      </template>

      <!-- 講座紹介マークダウン(編集不可エリア) -->
      <v-sheet
        v-for="(dividedContent, dividedIndex) in dividedContentsForView"
        :key="dividedIndex"
        :class="isEdit ? 'tabContent-trans' : 'tabContent'"
      >
        <template v-for="(content, detailIndex) in dividedContent" :key="detailIndex">
          <template v-if="content.type === 'md'">
            <div class="markdown" v-html="marked(content.value)" />
          </template>
          <template v-if="content.type === 'review'">
            <OrganismsPlanReviewPlanReviews v-if="planInfo" :plan-info="planInfo" class="mb-10" />
          </template>
        </template>
      </v-sheet>
      <AtomsScrollTop />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  @import "assets/markdown.scss";
  .tabContent,
  .tabContent-trans {
    background-color: rgb(var(--v-theme-monochrome-04));
    padding: 40px 40px;
    margin-top: 5px;
    margin-bottom: 20px;
    border-radius: 5px;
    position: relative;

    @include display_pc {
      padding: 40px 80px;
    }
    @include display_sp {
      padding: 20px 10px;
      margin-left: 0px;
      margin-right: 0px;
    }
  }
  .tabContent-trans {
    opacity: 0.5;
  }
  :deep(.markdown) {
    padding-top: 10px;
    @include markdown;
    @include markdown-plan;
  }
</style>
